
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	Avatar,
	RunnitUpload,
	AVATAR_USE_CASE,
	IMAGE_UPLOAD_MODE,
	NsfwThreshold,
} from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { ImageUploadMixin } from '@/mixins/ImageUploadMixin';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import { uploadMedia } from '@/utils/uploadApi';
import { functionsBaseUrl } from '@/firebase';

export default Vue.extend({
	name: 'VideoUpload',
	props: {
		mode: {
			type: String,
			required: true,
			validator: (val: string) => Object.keys(IMAGE_UPLOAD_MODE).includes(val),
		},
		avatarUseCase: {
			type: String,
			default: AVATAR_USE_CASE.RUNNIT,
			validator: (val: string) => Object.keys(AVATAR_USE_CASE).includes(val),
		},
		multiple: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },
		dropZone: { type: Boolean, default: true },
	},
	mixins: [
		ImageUploadMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	data () {
		return {
			FILE_SIZE_LIMIT: 104857600, // 100 MB in bytes for videos
			isUploading: false,
			isBeingDraggedOver: false,
			checkingNsfw: false,
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'dragAndDrop',
			'team',
		]),
		...mapGetters([
			'companyClubOfferBenefits',
		]),
		acceptedFileTypes () {
			return ['video/mp4', 'video/avi', 'video/quicktime', 'video/wmv', 'video/flv', 'video/webm'];
		},
		maxFileSizeMB () {
			return Math.round(this.FILE_SIZE_LIMIT / (1024 * 1024));
		},
		isDisabled () {
			const permissionDisabled = this.mode === IMAGE_UPLOAD_MODE.RUNNIT_UPLOAD && !this.hasGenerateRunnitPermission;
			return this.disabled || permissionDisabled;
		},
		isDraggingActive () {
			return this.dragAndDrop.isDragging && !this.isDisabled;
		},
		hasActiveDropZones () {
			return this.dragAndDrop.activeDropZones > 0;
		}
	},
	watch: {
		'dragAndDrop.isDragging': {
			immediate: true,
			handler (isDragging) {
				// Only set allowDrop if this component is a drop zone
				if (this.dropZone) {
					this.setAllowDrop(!this.isDisabled && isDragging);
				}
			}
		},
		'dragAndDrop.activeDropZones': {
			immediate: true,
			handler (activeDropZones) {
				// If we're in a dragging state but there are no active drop zones, restore this one
				// Only do this if this component is a drop zone
				if (this.dropZone && this.dragAndDrop.isDragging && activeDropZones === 0) {
					this.registerAsDropZone();
				}
			}
		},
		// Watch for changes to the dropZone prop
		dropZone: {
			immediate: false, // Don't run on component creation (handled in mounted)
			handler (newValue, oldValue) {
				if (newValue && !oldValue) {
					// If changing from not a drop zone to a drop zone, register
					this.registerAsDropZone();
				} else if (!newValue && oldValue) {
					// If changing from a drop zone to not a drop zone, unregister
					this.unregisterAsDropZone();
				}
			}
		}
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
			'setAllowDrop',
			'setIsDragging',
			'setDraggedItem',
			'addDropZone',
			'removeDropZone',
		]),
		clearAllSelectionStates () {
			const activeSelectionStates = this.orderedSelectionStates;

			// Exit selection mode for all active selection states
			activeSelectionStates.forEach(state => {
				if (state.triggeringId) {
					this.exitSelectionMode(state.triggeringId);
				}
			});
		},
		onUploadComplete (val: RunnitUpload[]) {
			const filesToEmit = this.multiple ? val : val[0];
			this.$emit('on-upload-complete', filesToEmit);
		},
		handleDragOver (ev) {
			if (this.isDisabled || !this.dropZone) return;

			// Check if it's a RunnitVideo being dragged
			if (this.dragAndDrop.isDragging && this.dragAndDrop.draggedItem && (this.dragAndDrop.draggedItem.type === 'RUNNIT_VIDEO')) {
				this.isBeingDraggedOver = true;
				ev.dataTransfer.dropEffect = 'copy';
			} else if (ev.dataTransfer.items && ev.dataTransfer.items.length > 0) {
				// Check if it's a file being dragged
				let hasValidFile = false;
				let hasOversizedFile = false;
				for (let i = 0; i < ev.dataTransfer.items.length; i++) {
					const item = ev.dataTransfer.items[i];
					if (item.kind === 'file' && item.type.startsWith('video/')) {
						hasValidFile = true;
						// Check file size if available
						try {
							const file = item.getAsFile();
							if (file && file.size > this.FILE_SIZE_LIMIT) {
								hasOversizedFile = true;
							}
						} catch (error) {
							// Ignore errors when getting file size during drag
							console.warn('Could not get file size during drag:', error);
						}
						break;
					}
				}

				if (hasValidFile) {
					if (hasOversizedFile) {
						// Show that file is too large
						ev.dataTransfer.dropEffect = 'none';
						this.isBeingDraggedOver = false;
					} else {
						this.isBeingDraggedOver = true;
						ev.dataTransfer.dropEffect = 'copy';
					}
				}
			}

			ev.preventDefault();
		},
		handleDragLeave (ev) {
			if (this.isUploading || this.isDisabled || !this.dropZone || this.checkingNsfw) return;
			this.isBeingDraggedOver = false;
			ev.preventDefault();
		},
		async handleDrop (ev) {
			if (this.isDisabled || !this.dropZone) return;
			this.isBeingDraggedOver = false;

			// Check if it's a RunnitVideo being dropped
			if (this.dragAndDrop.isDragging && this.dragAndDrop.draggedItem && this.dragAndDrop.draggedItem.type === 'RUNNIT_VIDEO') {
				// If we're in avatar mode, don't allow dropping already uploaded files
				if (this.mode === 'AVATAR') {
					console.warn('Dropping media not supported in avatar mode');
					this.updateSnackbar({
						status: SNACKBAR_STATUS.WARN,
						message: `Dropping media is not supported in avatar mode`,
						show: true,
					});

					// Reset drag state
					this.setIsDragging(false);
					this.setDraggedItem(null);
					return;
				}

				const draggedItem = this.dragAndDrop.draggedItem;

				try {
					this.$emit('on-upload-start');
					this.isUploading = true;

					if (draggedItem.nodeRunResult) {
						this.onUploadComplete([{ nodeRunResult: draggedItem.nodeRunResult }]);
					} else if (draggedItem.upload) {
						this.onUploadComplete([{ upload: draggedItem.upload }]);
					} else if (draggedItem.src) {
						// Handle case where we only have the video source URL
						try {
							const response = await fetch(draggedItem.src);
							const blob = await response.blob();

							// Generate unique filename with UUID and detect actual MIME type
							const uuid = this.$uuidv4();
							const fileExtension = this.getFileExtensionFromMimeType(blob.type) || 'mp4';
							const fileName = `video-${uuid}.${fileExtension}`;

							const file = new File([blob], fileName, { type: blob.type });
							const upload = await this.uploadFile(file);
							if (!upload) {
								return;
							}
							this.onUploadComplete([{ upload }]);
						} catch (e) {
							console.error('Error processing video from src:', e);
							throw e;
						}
					} else {
						console.error('Dropped item has no recognizable content:', draggedItem);
					}

					// Explicitly reset the drag state after processing
					this.setIsDragging(false);
					this.setDraggedItem(null);

					// Clear all active selection states
					this.clearAllSelectionStates();
				} catch (e) {
					console.error('Error processing dragged media:', e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Error processing dragged media`,
						show: true,
					});

					// Reset drag state even if there's an error
					this.setIsDragging(false);
					this.setDraggedItem(null);
				} finally {
					this.isUploading = false;
					this.$emit('on-upload-end');
				}

				// Ensure drag state is reset
				this.setIsDragging(false);
				this.setDraggedItem(null);

				// Clear all active selection states
				this.clearAllSelectionStates();
				return;
			}

			// Handle regular file drop
			let dt = ev.dataTransfer;
			let files = dt.files;
			await this.handleFiles(files);
		},
		async onHiddenFileInputChange (ev) {
			if (this.isUploading || this.checkingNsfw) return;
			this.isBeingDraggedOver = false;
			await this.handleFiles(ev.target.files);

			// Clear the selected file from the input
			if (this.$refs.fileInput) {
				this.$refs.fileInput.value = ''; // Reset the value of the input element
			}
		},
		openFileDialog () {
			if (this.isUploading || this.isDisabled || this.checkingNsfw) return;
			if (this.$refs.fileInput) {
				this.$refs.fileInput.click();
			}
		},
		formatFileSize (bytes: number): string {
			if (bytes === 0) return '0 Bytes';
			const k = 1024;
			const sizes = ['Bytes', 'KB', 'MB', 'GB'];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
		},
		getFileExtensionFromMimeType (mimeType: string): string {
			const mimeToExtension: { [key: string]: string } = {
				'video/mp4': 'mp4',
				'video/webm': 'webm',
				'video/ogg': 'ogv',
				'video/avi': 'avi',
				'video/quicktime': 'mov',
				'video/wmv': 'wmv',
				'video/flv': 'flv',
				'video/mkv': 'mkv',
				'video/3gp': '3gp',
				'video/m4v': 'm4v',
			};
			return mimeToExtension[mimeType] || 'mp4';
		},
		async handleFiles (fileList: FileList) {
			this.isBeingDraggedOver = false;
			const files = Array.from(fileList);
			if (!files || !files.length) return;

			// Filter by accepted file types
			const validFiles = files.filter(file => this.acceptedFileTypes.includes(file.type));
			const invalidFiles = files.filter(file => !this.acceptedFileTypes.includes(file.type));
			if (invalidFiles.length) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Invalid file format. Please upload ${this.acceptedFileTypes.join(', ')} files only`,
					show: true,
				});
			}
			if (!validFiles.length) return;

			// Validate file sizes
			const oversizedFiles = validFiles.filter(file => {
				return file.size > this.FILE_SIZE_LIMIT;
			});
			if (oversizedFiles.length) {
				const maxSizeMB = this.maxFileSizeMB;
				const fileDetails = oversizedFiles.map(file => `${file.name} (${this.formatFileSize(file.size)})`).join(', ');
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `File(s) too large: ${fileDetails}. Maximum allowed size is ${maxSizeMB}MB`,
					show: true,
				});
				return;
			}

			// Process only valid files (no resizing for videos)
			const processedFiles = validFiles;

			try {
				this.$emit('on-upload-start');
				this.isUploading = true;
				const uploads = await Promise.all(processedFiles.map(file => this.uploadFile(file)));
				const filteredUploads = uploads.filter(upload => !!upload);
				if (!filteredUploads.length) {
					return;
				}

				this.onUploadComplete(uploads);

				// Clear all active selection states
				this.clearAllSelectionStates();
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error uploading video`,
					show: true,
				});

				// Clear all active selection states
				this.clearAllSelectionStates();
			} finally {
				this.isUploading = false;
				this.$emit('on-upload-end');
			}
		},
		async isVideoNsfw (file) {
			this.checkingNsfw = true;
			const nsfwThreshold: NsfwThreshold = (this.companyClubOfferBenefits.isEnterprise && this.team?.nsfwThreshold) || 'DEFAULT';
			const frameInterval: number = 1.0; // Check every 1 second
			const reader: FileReader = new FileReader();

			return new Promise((resolve, reject) => {
				reader.onload = async () => {
					const result = reader.result;
					if (typeof result === 'string') {
						const base64Video = result.split(',')[1]; // Get base64 part
						try {
							const response = await fetch(`${functionsBaseUrl}/isVideoNsfw`, {
								method: 'POST',
								headers: {
									'Content-Type': 'application/json',
								},
								body: JSON.stringify({
									base64Videos: [base64Video],
									nsfwThreshold,
									frameInterval,
								}),
							});

							const rawResponseText = await response.text();
							let data: { error?: string, results?: Array<{ isNsfw?: boolean }> } = {};
							try {
								data = rawResponseText ? JSON.parse(rawResponseText) : {};
							} catch (parseError) {
								console.error('isVideoNsfw returned non-JSON response:', {
									status: response.status,
									statusText: response.statusText,
									responsePreview: rawResponseText?.slice(0, 200),
								}, parseError);
								this.checkingNsfw = false;
								resolve(false);
								return;
							}

							if (!response.ok) {
								console.error('isVideoNsfw request failed:', {
									status: response.status,
									statusText: response.statusText,
									error: data?.error,
								});
								this.checkingNsfw = false;
								resolve(false);
								return;
							}

							// Check if we have results and at least one result
							if (data.results && data.results.length > 0) {
								this.checkingNsfw = false;
								resolve(data.results[0].isNsfw);
							} else {
								this.checkingNsfw = false;
								resolve(false); // Default to false if no clear result
							}
						} catch (error) {
							console.error('Error calling isVideoNsfw:', error);
							this.checkingNsfw = false;
							resolve(false); // Default to false on error
						}
					} else {
						this.checkingNsfw = false;
						reject(new Error('Failed to read file as a string'));
					}
				};
				reader.onerror = reject;
				reader.readAsDataURL(file);
			});
		},
		async uploadFile (file) {
			const nsfwResults = await this.isVideoNsfw(file);
			if (nsfwResults) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error uploading video: triggered NSFW filter`,
					show: true,
				});
				return null;
			}
			if (this.mode === 'RUNNIT_UPLOAD') {
				return this.handleRunnitUpload(file);
			} else if (this.mode === 'AVATAR') {
				return this.handleAvatar(file);
			}
		},
		async handleRunnitUpload (file: File) {
			// Calculate hash of the file data
			const fileData = await file.arrayBuffer();
			const hashBuffer = await crypto.subtle.digest('SHA-256', fileData);
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
			const { width, height, duration } = await this.getVideoMetaData(file);
			return await uploadMedia({
				kind: 'runnitUpload',
				file,
				hashHex,
				uploadType: 'VID',
				width,
				height,
				duration,
				teamId: null,
			}) as RunnitUpload;
		},
		async handleAvatar (file: File) {
			// NOTE: Videos are not typically used for avatars, but keeping for consistency
			// Calculate hash of the file data
			const fileData = await file.arrayBuffer();
			const hashBuffer = await crypto.subtle.digest('SHA-256', fileData);
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
			const { width, height, duration } = await this.getVideoMetaData(file);
			return await uploadMedia({
				kind: 'avatar',
				file,
				hashHex,
				uploadType: 'VID',
				width,
				height,
				duration,
				useCase: this.avatarUseCase || AVATAR_USE_CASE.RUNNIT,
				teamId: null,
			}) as Avatar;
		},
		async getVideoMetaData (file: File) {
			return new Promise((resolve, reject) => {
				const video = document.createElement('video');
				video.preload = 'metadata';

				video.onloadedmetadata = () => {
					window.URL.revokeObjectURL(video.src);
					resolve({
						name: file.name,
						contentType: file.type,
						size: file.size,
						width: video.videoWidth,
						height: video.videoHeight,
						duration: video.duration,
					});
				};

				video.onerror = () => {
					window.URL.revokeObjectURL(video.src);
					reject(new Error('Failed to load video metadata'));
				};

				video.src = URL.createObjectURL(file);
			});
		},
		handleDocumentDrop (ev) {
			// Only handle the document drop if we're in a dragging state, this component is still active,
			// and this component is a drop zone
			if (this.dropZone && this.dragAndDrop.isDragging && this.hasActiveDropZones) {

				// Don't process drops in avatar mode
				if (this.mode === 'AVATAR' && this.dragAndDrop.draggedItem && this.dragAndDrop.draggedItem.type === 'RUNNIT_VIDEO') {

					// Reset drag state
					this.setIsDragging(false);
					this.setDraggedItem(null);
					return;
				}

				// Prevent default browser behavior
				ev.preventDefault();

				// Process the drop using our component's handler
				this.handleDrop(ev);
			}
		},
		handleGlobalDragEnd () {
			// This ensures the drag state is reset even if the component unmounts during drag
			// Only affect the global state if this component is a drop zone
			if (this.dropZone) {
				this.cleanupDragState();
			}
		},
		cleanupDragState () {
			// Only cleanup if this component is a drop zone
			if (this.dropZone) {
				// Reset the drag state
				this.setIsDragging(false);
				this.setDraggedItem(null);

				// Clear all active selection states
				this.clearAllSelectionStates();
			}
		},
		// Method to register this component as a drop zone
		registerAsDropZone () {
			if (this.dropZone) {
				this.addDropZone();
			}
		},
		// Method to unregister this component as a drop zone
		unregisterAsDropZone () {
			if (this.dropZone) {
				this.removeDropZone();
			}
		},
	},
	components: {
	},
	created () {
		// If we're in the middle of a drag operation and this is a drop zone, register immediately
		if (this.dropZone && this.dragAndDrop && this.dragAndDrop.isDragging) {
			this.registerAsDropZone();
		}
	},
	mounted () {
		// Only register as a drop zone if the dropZone prop is true
		if (this.dropZone) {
			this.registerAsDropZone();
		}

		// Add event listener for dragend to ensure we clean up if the component unmounts during drag
		window.addEventListener('dragend', this.handleGlobalDragEnd);

		// Add document-level drop handler as a fallback
		document.addEventListener('drop', this.handleDocumentDrop);

		// Check if we're in the middle of a drag operation and restore state if needed
		if (this.dropZone && this.dragAndDrop.isDragging && !this.hasActiveDropZones) {
			this.registerAsDropZone();
		}
	},
	beforeDestroy () {
		// Only unregister as a drop zone if the dropZone prop is true
		if (this.dropZone) {
			// Only unregister as a drop zone if we're not in the middle of a drag operation
			if (!this.dragAndDrop.isDragging) {
				this.unregisterAsDropZone();
			}
		}

		// Remove the global event listeners
		window.removeEventListener('dragend', this.handleGlobalDragEnd);
		document.removeEventListener('drop', this.handleDocumentDrop);
	},
});
